<template>
  <section>
    <div class="auth-form">
      <form class="form" @submit.prevent="restorePassword">
        <h1 class="form__title">
          {{ $t('CP.restorePassword') }}
        </h1>
        <p class="form__description">
          {{ $t('restorePasswordForm.description') }}
        </p>
        <div class="form__fields">
          <input-app
            :placeholder="$t('restorePasswordForm.placeholderPhone')"
            name="phone"
            :label-text="$t('restorePasswordForm.labelPhoneNumber') + ' :'"
            type="tel"
            v-model="$v.phone.$model"
            :error="$v.$dirty && $v.phone.$error"
          />
          <div v-if="$v.$dirty && $v.phone.$error" class="invalid-message">
            <span v-if="!$v.phone.required">
              {{ $t('validateText.required') }}
            </span>
            <span v-if="!$v.phone.minLength">
              {{ $t('validateText.incorrectPhone') }}
            </span>
          </div>
          <input-app
            :placeholder="$t('restorePasswordForm.placeholderPassword')"
            name="password"
            v-model="password"
            :label-text="$t('restorePasswordForm.labelPassword') + ':'"
            type="password"
            :error="$v.$dirty && $v.password.$error"
          />
          <div v-if="$v.$dirty && $v.password.$error" class="invalid-message">
            <span v-if="!$v.password.required">
              {{ $t('validateText.required') }}
            </span>
            <span v-if="!$v.password.minLength">
              {{ $t('validateText.shortPassword') }}
            </span>
          </div>
          <cloudflare-turnstile @verified="handleTurnstileVerified"/>
        </div>
        <button-app
          v-if="(!submitted || $v.$invalid) && !error || (!submitted && error)"
          type="submit"
        >
          {{ $t('restorePasswordForm.confirm') }}
        </button-app>
        <div v-if="submitted && !$v.$invalid || error" class="register-finish">
          <input-app
            v-if="!error"
            :placeholder="$t('restorePasswordForm.placeholderSendCode')"
            name="code"
            v-model="code"
            :label-text="$t('restorePasswordForm.labelSendCode') + ':'"
            type="number"
            :error="submitted && $v.password.$error"
          />
          <p v-if="!error" class="text__info-sms">
            {{ $t('restorePasswordForm.weSentCode') }}
          </p>
          <p v-if="!error && sendCodeTimer !== 61" class="text__repeat-count_info-sms">
            {{ $t('restorePasswordForm.sendCodeTimer') }} {{sendCodeTimer}}
          </p>
          <button-app
            v-if="submitted && error || submitted && sendCodeTimer === 61"
            type="button"
            @clicked="sendCode(phone)"
          >
            {{ $t('restorePasswordForm.sendCodeAgain') }}
          </button-app>
          <button-app v-if="!error" type="submit">
            {{ $t('restorePasswordForm.changePassword') }}
          </button-app>
        </div>
        <transition name="form__error__fade">
          <p v-if="error && error === 'too-many-requests-for-number'" class="form__error">
            {{ $t('validateText.errorRestorePasswordCodeTooManyRequests') }}
          </p>
          <p v-if="error && error === 'ErrorCode.USER_DOESNT_EXIST'" class="form__error">
            {{ $t('validateText.errorRestorePasswordCodeUserDoesntExist') }}
          </p>
          <p v-else-if="error" class="form__error">
            {{ $t('validateText.errorRestorePasswordSubmit') }}
          </p>
        </transition>
      </form>
       <p class="account-info-answer">
         <router-link class="sign-up-link" :to="'signin'">
           {{ $t('restorePasswordForm.loginLink') }}
         </router-link>
         {{ $t('restorePasswordForm.or') }}
         <router-link class="sign-up-link" :to="'signup'">
           {{ $t('restorePasswordForm.registrationLink') }}
         </router-link>
       </p>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { minLength, required } from 'vuelidate/lib/validators';
import { CLEAR_AUTH_ERROR, REG_REQUEST, SEND_CODE_REQUEST } from '@/store/auth/types';
import CloudflareTurnstile from '@/components/CloudflareTurnstile/CouldflareTurnstile';
import ButtonApp from '@/components/Button/ButtonApp';
import InputApp from '@/components/Input/InputApp';

export default {
  name: 'RestorePasswordForm',
  components: {
    ButtonApp,
    InputApp,
    CloudflareTurnstile,
  },
  data() {
    return {
      phone: '',
      password: '',
      submitted: false,
      code: '',
      attempt: 0,
      sendCodeTimer: 61,
      codeTimer: null,
      turnstileToken: null,
    };
  },
  validations: {
    phone: {
      required,
      minLength: minLength(11),
    },
    password: {
      required,
      minLength: minLength(5),
    },
  },
  methods: {
    handleTurnstileVerified(token) {
      this.turnstileToken = token;
    },
    restorePassword() {
      try {
        this.$store.commit(CLEAR_AUTH_ERROR);
        const { phone, password, code } = this;
        this.$v.$touch();
        if (this.$v.$invalid) {
          return;
        }
        if (!this.submitted) {
          this.sendCode(phone)
            .then(() => {
              this.submitted = true;
            })
            .catch((e) => {
              // eslint-disable-next-line no-console
              console.log(e.message);
              this.submitted = false;
            });
          return;
        }
        this.$store.dispatch(REG_REQUEST, {
          purpose: 'restore_password',
          phone,
          password,
          code,
        })
          .then(() => {
            this.$router.push('/events');
          })
          .catch(() => {
            clearInterval(this.codeTimer);
            this.codeTimer = null;
            this.sendCodeTimer = 61;
            this.submitted = false;
            // eslint-disable-next-line no-console
            console.log('register error');
          });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e.message);
      }
    },
    async sendCode(phone) {
      try {
        this.startTimer();
        if (this.attempt < 3) {
          this.attempt += 1;
        }
        this.$store.dispatch(SEND_CODE_REQUEST, {
          purpose: 'restore_password',
          attempt: this.attempt,
          phone,
          turnstileToken: this.turnstileToken,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e.message);
      }
    },
    startTimer() {
      if (this.codeTimer) return;
      this.codeTimer = setInterval(() => {
        this.sendCodeTimer -= 1;
        if (this.sendCodeTimer <= 0) {
          clearInterval(this.codeTimer);
          this.codeTimer = null;
          this.sendCodeTimer = 61;
        }
      }, 1000);
    },
  },
  computed: {
    ...mapState({
      error: (state) => state.auth.error,
    }),
  },
};
</script>

<style lang="scss" scoped>
.account-info-answer {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}
</style>
